// Overlapcontrole: een post die normaal al inbegrepen is in een andere post op dezelfde
// calculatie wordt niet stil meebetaald (SPEC_wijzigingen 5.4, open punt 10.5).
// Voorbeeld Nedelco: "Plaatsen van lood" zit in "Netto dak met isolatie".
import type { MatchedLine, PricelistItem } from './types.js';

export interface OverlapVondst {
  lineId: string;
  naamArbeid: string;
  itemCode: string;
  inbegrepenInCode: string;
  inbegrepenInNaam: string;
}

export type OverlapBeslissing = 'betalen' | 'niet_betalen';

/** Sleutel voor het geleerde geheugen: "post→post waarin hij inbegrepen is". */
export function overlapSleutel(itemCode: string, inbegrepenInCode: string): string {
  return `${itemCode}→${inbegrepenInCode}`;
}

function itemsVanLijn(line: MatchedLine, byId: Map<string, PricelistItem>): PricelistItem[] {
  const out: PricelistItem[] = [];
  for (const s of line.subItems) {
    const it = byId.get(s.plItemId);
    if (it && !out.includes(it)) out.push(it);
  }
  return out;
}

export function detecteerOverlap(matchedLines: MatchedLine[], items: PricelistItem[]): OverlapVondst[] {
  const byId = new Map(items.map((it) => [it.id, it] as const));
  // Welke postcodes staan er op de calculatie, en onder welke naam? Een geweigerde post telt
  // niet als "aanwezig" — hij wordt toch niet betaald.
  const aanwezig = new Map<string, string>();
  for (const l of matchedLines) {
    if (l.overlap?.status === 'geweigerd') continue;
    for (const it of itemsVanLijn(l, byId)) if (it.code) aanwezig.set(it.code, l.naamArbeid);
  }
  const vondsten: OverlapVondst[] = [];
  for (const l of matchedLines) {
    for (const it of itemsVanLijn(l, byId)) {
      if (!it.code || !it.inbegrepenIn?.length) continue;
      const container = it.inbegrepenIn.find((c) => c !== it.code && aanwezig.has(c));
      if (!container) continue;
      vondsten.push({
        lineId: l.id,
        naamArbeid: l.naamArbeid,
        itemCode: it.code,
        inbegrepenInCode: container,
        inbegrepenInNaam: aanwezig.get(container)!,
      });
      break;
    }
  }
  return vondsten;
}

/**
 * Zet de overlapstatus op de lijnen. Nieuwe overlap → 'open' (telt niet mee tot de gebruiker
 * beslist), tenzij het geheugen al een beslissing heeft: 'betalen' → bevestigd,
 * 'niet_betalen' → geweigerd. Een bestaande beslissing voor dezelfde overlap blijft staan;
 * overlap die verdwenen is (de post waarin hij zat staat er niet meer) wordt opgeheven.
 */
export function pasOverlapToe(
  matchedLines: MatchedLine[],
  items: PricelistItem[],
  geleerd: Map<string, OverlapBeslissing> = new Map(),
): { matchedLines: MatchedLine[]; vondsten: OverlapVondst[] } {
  const vondsten = detecteerOverlap(matchedLines, items);
  const perLijn = new Map(vondsten.map((v) => [v.lineId, v] as const));
  const out: MatchedLine[] = matchedLines.map((l) => {
    const v = perLijn.get(l.id);
    if (!v) {
      if (!l.overlap) return l;
      const { overlap: _weg, ...rest } = l;
      void _weg;
      return rest;
    }
    if (l.overlap && l.overlap.inbegrepenInCode === v.inbegrepenInCode && l.overlap.itemCode === v.itemCode) return l;
    const beslissing = geleerd.get(overlapSleutel(v.itemCode, v.inbegrepenInCode));
    return {
      ...l,
      overlap: {
        itemCode: v.itemCode,
        inbegrepenInCode: v.inbegrepenInCode,
        inbegrepenInNaam: v.inbegrepenInNaam,
        status: beslissing === 'betalen' ? 'bevestigd' : beslissing === 'niet_betalen' ? 'geweigerd' : 'open',
      },
    };
  });
  return { matchedLines: out, vondsten };
}

/** Beslissingen die de gebruiker op deze calculatie nam — om te leren (SPEC_calculatie 10). */
export function overlapBeslissingen(matchedLines: MatchedLine[]): Array<{ sleutel: string; beslissing: OverlapBeslissing }> {
  const out: Array<{ sleutel: string; beslissing: OverlapBeslissing }> = [];
  for (const l of matchedLines) {
    if (!l.overlap || l.overlap.status === 'open') continue;
    out.push({
      sleutel: overlapSleutel(l.overlap.itemCode, l.overlap.inbegrepenInCode),
      beslissing: l.overlap.status === 'bevestigd' ? 'betalen' : 'niet_betalen',
    });
  }
  return out;
}
